import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';

// Types for the backend structure
interface Project {
  id: number;
  title: string;
  description: string;
  genre: string;
  status: string;
  current_step: string;
  total_episodes?: number;
  total_characters?: number;
  total_scenes?: number;
  scenario_structure?: string;
  created_at: string;
  updated_at: string;
}

interface CharacterVariation {
  id: number;
  character_id: number;
  variation_name: string;
  variation_prompt: string;
  image_url?: string;
  status: string;
  seed?: number;
}

interface Character {
  id: number;
  name: string;
  reference_description: string;
  reference_prompt?: string;
  variations_json?: string;
  status: string;
  order_index: number;
  front_image_url?: string;
  character_seed?: number;
  variations?: CharacterVariation[];
}

interface Environment {
  id: number;
  name: string;
  reference_description: string;
  image_url?: string;
  status: string;
  seed?: number;
}

interface Episode {
  id: number;
  project_id: number;
  episode_number: number;
  title: string;
  start_scene_number: number;
  end_scene_number: number;
}

interface Scene {
  id: number;
  project_id: number;
  episode_id: number;
  scene_number: number;
  description: string;
  character_name?: string;
  environment_name?: string;
  character_key?: string;
  character_variation?: string;
  character_id: number;
  environment_id: number;
  first_frame_description: string;
  is_continue: boolean;
  veo3_prompt: string;
  frame_url?: string;
  frame_seed?: number;
  status: string;
  order_index: number;
  episode_number?: number;
}

interface SceneVideo {
  id: number;
  scene_id: number;
  provider: string;
  video_url?: string;
  status: string;
  seed?: number;
  prompt?: string;
  duration?: number;
  resolution?: string;
  error_message?: string;
  source_image_url?: string;
  is_from_uploaded_image?: boolean;
  created_at: string;
  updated_at: string;
}

interface SceneAudio {
  id: number;
  scene_id: number;
  video_id: number;
  video_provider: string;
  audio_url?: string;
  status: string;
  text_prompt?: string;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

interface GlobalVideo {
  id: number;
  filename: string;
  original_video_url?: string;
  processed_audio_url?: string;
  status: string;
  text_prompt?: string;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

interface StoryboardData {
  project: Project;
  characters: Character[];
  environments: Environment[];
  episodes: Episode[];
  scenes: Scene[];
  scenario_structure?: any;
  statistics?: {
    total_scenes: number;
    total_episodes: number;
    total_characters: number;
    total_environments: number;
    completed_scenes: number;
    progress_percentage: number;
  };
}

interface GenerationStatus {
  project_id: number;
  overall_status: string;
  current_phase: string;
  current_step: string;
  progress_percentage: number;
  total_steps: number;
  completed_steps: number;
  characters_status: { [id: number]: string };
  environments_status: { [id: number]: string };
  scenes_status: { [id: number]: string };
  error_message?: string;
}

interface QuickStatus {
  status: string;
  current_step: string;
  timestamp: string;
}

interface ProgressStatus {
  progress_percentage: number;
  current_phase: string;
  current_step: string;
  completed_steps: number;
  total_steps: number;
}

interface ModalData {
  imageUrl: string;
  title: string;
  subtitle: string;
  isOpen: boolean;
}

interface SceneModalData {
  isOpen: boolean;
  scene: Scene | null;
}

interface VEO3ModalData {
  isOpen: boolean;
  scene: Scene | null;
}

interface VideoModalData {
  isOpen: boolean;
  scene: Scene | null;
  selectedProvider: 'seedance' | 'minimax';
  videos: SceneVideo[];
  isGenerating: boolean;
  uploadedImage: File | null;
  uploadedImageUrl: string;
}

interface AudioModalData {
  isOpen: boolean;
  scene: Scene | null;
  selectedVideoProvider: 'seedance' | 'minimax';
  textPrompt: string;
  audioRecords: SceneAudio[];
  isGenerating: boolean;
}

interface GlobalVideoModalData {
  isOpen: boolean;
  activeTab: 'upload' | 'list';
  uploadFile: File | null;
  uploadTextPrompt: string;
  isUploading: boolean;
  globalVideos: GlobalVideo[];
}

interface SceneImageEdit {
  id: number;
  scene_id: number;
  original_image_url: string;
  edited_image_url?: string;
  model_type: string;
  prompt: string;
  status: string;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

// НОВЫЙ ИНТЕРФЕЙС ДЛЯ СОСТОЯНИЯ МОДАЛЬНОГО ОКНА РЕДАКТИРОВАНИЯ
interface ImageEditModalData {
  isOpen: boolean;
  scene: Scene | null;
  selectedModel: 'nano-banana' | 'flux-pro' | 'qwen';
  prompt: string;
  edits: SceneImageEdit[];
  isGenerating: boolean;
  pollingEdits: Set<number>;
}

// API Base URL
const API_BASE = 'https://aigenerate.golive.mobi/api';

// Auth credentials
const AUTH_CREDENTIALS = {
  username: 'chjsh762',
  password: 'c78s78iw3io9djke'
};

const AUTH_COOKIE_NAME = 'anime_auth_token';
const AUTH_DURATION = 15 * 60 * 1000; // 15 minutes in milliseconds

type ViewMode = 'login' | 'list' | 'create' | 'upload-scenario' | 'generating' | 'storyboard';

function App() {

   const [imageEditModal, setImageEditModal] = useState<ImageEditModalData>({
    isOpen: false,
    scene: null,
    selectedModel: 'nano-banana',
    prompt: '',
    edits: [],
    isGenerating: false,
    pollingEdits: new Set(),
  });



  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [authError, setAuthError] = useState<string | null>(null);

  const [viewMode, setViewMode] = useState<ViewMode>('login');
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [storyboardData, setStoryboardData] = useState<StoryboardData | null>(null);
  const [generationStatus, setGenerationStatus] = useState<GenerationStatus | null>(null);
  const [quickStatus, setQuickStatus] = useState<QuickStatus | null>(null);
  const [progressStatus, setProgressStatus] = useState<ProgressStatus | null>(null);
  const [activePolling, setActivePolling] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Modal states
  const [modal, setModal] = useState<ModalData>({
    imageUrl: '',
    title: '',
    subtitle: '',
    isOpen: false
  });

  const [sceneModal, setSceneModal] = useState<SceneModalData>({
    isOpen: false,
    scene: null
  });

  const [veo3Modal, setVEO3Modal] = useState<VEO3ModalData>({
    isOpen: false,
    scene: null
  });

  const [videoModal, setVideoModal] = useState<VideoModalData>({
    isOpen: false,
    scene: null,
    selectedProvider: 'seedance',
    videos: [],
    isGenerating: false,
    uploadedImage: null,
    uploadedImageUrl: ''
  });

  const [audioModal, setAudioModal] = useState<AudioModalData>({
    isOpen: false,
    scene: null,
    selectedVideoProvider: 'seedance',
    textPrompt: '',
    audioRecords: [],
    isGenerating: false
  });

  // Global video modal state
  const [globalVideoModal, setGlobalVideoModal] = useState<GlobalVideoModalData>({
    isOpen: false,
    activeTab: 'upload',
    uploadFile: null,
    uploadTextPrompt: '',
    isUploading: false,
    globalVideos: []
  });

  // Form state
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [scenarioJson, setScenarioJson] = useState('');

  // Polling for scene regeneration
  const [regeneratingScenes, setRegeneratingScenes] = useState<Set<number>>(new Set());

  // Video/Audio polling
  const [videoPolling, setVideoPolling] = useState<Set<string>>(new Set()); // scene_id:provider format
  const [audioPolling, setAudioPolling] = useState<Set<string>>(new Set()); // scene_id:provider format

  // Global video polling
  const [globalVideoPolling, setGlobalVideoPolling] = useState<Set<number>>(new Set());

  // Auth functions
  const checkAuthCookie = () => {
    const authCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith(AUTH_COOKIE_NAME + '='));
    
    if (authCookie) {
      const tokenData = authCookie.split('=')[1];
      try {
        const parsed = JSON.parse(decodeURIComponent(tokenData));
        const now = Date.now();
        if (parsed.expires > now) {
          setIsAuthenticated(true);
          setViewMode('list');
          return true;
        } else {
          // Token expired, remove cookie
          document.cookie = `${AUTH_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        }
      } catch (err) {
        console.error('Error parsing auth cookie:', err);
      }
    }
    return false;
  };

  const setAuthCookie = () => {
    const expires = Date.now() + AUTH_DURATION;
    const tokenData = JSON.stringify({ expires });
    document.cookie = `${AUTH_COOKIE_NAME}=${encodeURIComponent(tokenData)}; path=/; max-age=${AUTH_DURATION / 1000}`;
    
    // Auto logout after 15 minutes
    setTimeout(() => {
      logout();
    }, AUTH_DURATION);
  };

  const login = () => {
    if (loginForm.username === AUTH_CREDENTIALS.username && 
        loginForm.password === AUTH_CREDENTIALS.password) {
      setIsAuthenticated(true);
      setAuthError(null);
      setAuthCookie();
      setViewMode('list');
      setLoginForm({ username: '', password: '' });
    } else {
      setAuthError('Invalid username or password');
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    setViewMode('login');
    setSelectedProject(null);
    setStoryboardData(null);
    document.cookie = `${AUTH_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    setLoginForm({ username: '', password: '' });
  };

  // Success message helper
  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  // Modal functions
  const openModal = (imageUrl: string, title: string, subtitle: string) => {
    setModal({
      imageUrl,
      title,
      subtitle,
      isOpen: true
    });
  };

  const closeModal = () => {
    setModal({
      imageUrl: '',
      title: '',
      subtitle: '',
      isOpen: false
    });
  };

  const openSceneModal = (scene: Scene) => {
    setSceneModal({
      isOpen: true,
      scene
    });
  };

  const closeSceneModal = () => {
    setSceneModal({
      isOpen: false,
      scene: null
    });
  };

  const openVEO3Modal = (scene: Scene) => {
    setVEO3Modal({
      isOpen: true,
      scene
    });
  };

  const closeVEO3Modal = () => {
    setVEO3Modal({
      isOpen: false,
      scene: null
    });
  };

  const openVideoModal = async (scene: Scene) => {
    // Fetch existing videos for this scene
    try {
      const response = await fetch(`${API_BASE}/projects/${scene.project_id}/scenes/${scene.id}/videos`);
      if (response.ok) {
        const data = await response.json();
        setVideoModal({
          isOpen: true,
          scene,
          selectedProvider: 'seedance',
          videos: data.videos || [],
          isGenerating: false,
          uploadedImage: null,
          uploadedImageUrl: ''
        });
      } else {
        setVideoModal({
          isOpen: true,
          scene,
          selectedProvider: 'seedance',
          videos: [],
          isGenerating: false,
          uploadedImage: null,
          uploadedImageUrl: ''
        });
      }
    } catch (err) {
      console.error('Error fetching videos:', err);
      setVideoModal({
        isOpen: true,
        scene,
        selectedProvider: 'seedance',
        videos: [],
        isGenerating: false,
        uploadedImage: null,
        uploadedImageUrl: ''
      });
    }
  };

  const closeVideoModal = () => {
    setVideoModal({
      isOpen: false,
      scene: null,
      selectedProvider: 'seedance',
      videos: [],
      isGenerating: false,
      uploadedImage: null,
      uploadedImageUrl: ''
    });
  };

  const openAudioModal = async (scene: Scene) => {
    // Fetch both videos and audio records for this scene
    try {
      const [audioResponse, videosResponse] = await Promise.all([
        fetch(`${API_BASE}/projects/${scene.project_id}/scenes/${scene.id}/audio`),
        fetch(`${API_BASE}/projects/${scene.project_id}/scenes/${scene.id}/videos`)
      ]);

      const audioData = audioResponse.ok ? await audioResponse.json() : { audio_records: [] };
      const videosData = videosResponse.ok ? await videosResponse.json() : { videos: [] };

      setAudioModal({
        isOpen: true,
        scene,
        selectedVideoProvider: 'seedance',
        textPrompt: '',
        audioRecords: audioData.audio_records || [],
        isGenerating: false
      });

      // Also update video modal data for reference
      setVideoModal(prev => ({
        ...prev,
        videos: videosData.videos || []
      }));

    } catch (err) {
      console.error('Error fetching audio/videos:', err);
      setAudioModal({
        isOpen: true,
        scene,
        selectedVideoProvider: 'seedance',
        textPrompt: '',
        audioRecords: [],
        isGenerating: false
      });
    }
  };

  const closeAudioModal = () => {
    setAudioModal({
      isOpen: false,
      scene: null,
      selectedVideoProvider: 'seedance',
      textPrompt: '',
      audioRecords: [],
      isGenerating: false
    });
  };

  // Global video modal functions
  const openGlobalVideoModal = () => {
    setGlobalVideoModal(prev => ({
      ...prev,
      isOpen: true
    }));
    fetchGlobalVideos();
  };

  const closeGlobalVideoModal = () => {
    setGlobalVideoModal({
      isOpen: false,
      activeTab: 'upload',
      uploadFile: null,
      uploadTextPrompt: '',
      isUploading: false,
      globalVideos: []
    });
  };

  const openImageEditModal = async (scene: Scene) => {
    setImageEditModal(prev => ({ ...prev, isOpen: true, scene }));
    await fetchImageEdits(scene.id);
  };

  const closeImageEditModal = () => {
    setImageEditModal({
      isOpen: false,
      scene: null,
      selectedModel: 'nano-banana',
      prompt: '',
      edits: [],
      isGenerating: false,
      pollingEdits: new Set(),
    });
  };

  const fetchImageEdits = async (sceneId: number) => {
    try {
      const response = await fetch(`${API_BASE}/projects/${selectedProject?.id}/scenes/${sceneId}/image-edits`);
      if (response.ok) {
        const data = await response.json();
        setImageEditModal(prev => ({ ...prev, edits: data.image_edits || [] }));
      }
    } catch (err) {
      console.error('Error fetching image edits:', err);
    }
  };

  const startImageEdit = async () => {
    if (!imageEditModal.scene || !imageEditModal.prompt.trim()) return;

    try {
      setImageEditModal(prev => ({ ...prev, isGenerating: true }));

      const response = await fetch(`${API_BASE}/projects/${selectedProject?.id}/scenes/${imageEditModal.scene.id}/edit-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model_type: imageEditModal.selectedModel,
          prompt: imageEditModal.prompt,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to start image editing');
      }

      const result = await response.json();
      showSuccess(`Image editing started with ${imageEditModal.selectedModel}!`);
      
      // Сбросить промпт и обновить список
      setImageEditModal(prev => ({ ...prev, prompt: '' }));
      await fetchImageEdits(imageEditModal.scene.id);

      // Начать поллинг для нового эдита
      setImageEditModal(prev => ({
        ...prev,
        pollingEdits: new Set([...Array.from(prev.pollingEdits), result.edit_id]),
      }));
      pollImageEditStatus(imageEditModal.scene.id, result.edit_id);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start image editing');
    } finally {
      setImageEditModal(prev => ({ ...prev, isGenerating: false }));
    }
  };

  const pollImageEditStatus = async (sceneId: number, editId: number) => {
    try {
      const response = await fetch(`${API_BASE}/projects/${selectedProject?.id}/scenes/${sceneId}/image-edits/${editId}/status`);
      if (response.ok) {
        const data = await response.json();
        
        // Обновляем конкретный эдит в списке
        setImageEditModal(prev => ({
          ...prev,
          edits: prev.edits.map(edit => edit.id === editId ? data : edit),
        }));

        if (data.status === 'completed' || data.status === 'error') {
          // Остановить поллинг
          setImageEditModal(prev => {
            const newPollingEdits = new Set(prev.pollingEdits);
            newPollingEdits.delete(editId);
            return { ...prev, pollingEdits: newPollingEdits };
          });
          if (data.status === 'completed') showSuccess('Image edit completed!');
          if (data.status === 'error') setError(`Image edit failed: ${data.error_message}`);
        } else {
          // Продолжить поллинг
          setTimeout(() => pollImageEditStatus(sceneId, editId), 3000);
        }
      }
    } catch (err) {
      console.error(`Error polling edit status for ${editId}:`, err);
      setTimeout(() => pollImageEditStatus(sceneId, editId), 5000);
    }
  };

  const fetchGlobalVideos = async () => {
    try {
      const response = await fetch(`${API_BASE}/global-videos`);
      if (response.ok) {
        const videos = await response.json();
        setGlobalVideoModal(prev => ({
          ...prev,
          globalVideos: videos
        }));
      }
    } catch (err) {
      console.error('Error fetching global videos:', err);
    }
  };

  // Handle escape key to close modals
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (modal.isOpen) {
          closeModal();
        } else if (sceneModal.isOpen) {
          closeSceneModal();
        } else if (veo3Modal.isOpen) {
          closeVEO3Modal();
        } else if (videoModal.isOpen) {
          closeVideoModal();
        } else if (audioModal.isOpen) {
          closeAudioModal();
        } else if (globalVideoModal.isOpen) {
          closeGlobalVideoModal();
        } else if (imageEditModal.isOpen) { // <-- ДОБАВИТЬ ЭТУ СТРОКУ
          closeImageEditModal();          // <-- ДОБАВИТЬ ЭТУ СТРОКУ
        }
      }
    };

    const isAnyModalOpen = modal.isOpen || sceneModal.isOpen || veo3Modal.isOpen || 
                          videoModal.isOpen || audioModal.isOpen || globalVideoModal.isOpen ||
                          imageEditModal.isOpen;

    if (isAnyModalOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [modal.isOpen, sceneModal.isOpen, veo3Modal.isOpen, videoModal.isOpen, audioModal.isOpen, globalVideoModal.isOpen]);

  // API functions
  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/projects`);
      if (!response.ok) throw new Error('Failed to fetch projects');
      const data = await response.json();
      setProjects(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProjectDetails = useCallback(async (projectId: number) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/projects/${projectId}`);
      if (!response.ok) throw new Error('Failed to fetch project details');
      const data = await response.json();
      setSelectedProject(data.project);
      setStoryboardData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch project details');
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh functions for modals
  const refreshVideoModal = async (sceneId: number) => {
    try {
      const response = await fetch(`${API_BASE}/projects/${selectedProject?.id}/scenes/${sceneId}/videos`);
      if (response.ok) {
        const data = await response.json();
        setVideoModal(prev => ({
          ...prev,
          videos: data.videos || []
        }));
      }
    } catch (err) {
      console.error('Error refreshing videos:', err);
    }
  };

  const refreshAudioModal = async (sceneId: number) => {
    try {
      const [audioResponse, videosResponse] = await Promise.all([
        fetch(`${API_BASE}/projects/${selectedProject?.id}/scenes/${sceneId}/audio`),
        fetch(`${API_BASE}/projects/${selectedProject?.id}/scenes/${sceneId}/videos`)
      ]);

      const audioData = audioResponse.ok ? await audioResponse.json() : { audio_records: [] };
      const videosData = videosResponse.ok ? await videosResponse.json() : { videos: [] };

      setAudioModal(prev => ({
        ...prev,
        audioRecords: audioData.audio_records || []
      }));

      // Also update video data for audio modal
      setVideoModal(prev => ({
        ...prev,
        videos: videosData.videos || []
      }));
    } catch (err) {
      console.error('Error refreshing audio:', err);
    }
  };

  // Image upload handler for video modal
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setVideoModal(prev => ({
        ...prev,
        uploadedImage: file,
        uploadedImageUrl: imageUrl
      }));
    }
  };

  const generateVideo = async (sceneId: number, provider: 'seedance' | 'minimax') => {
    // Проверяем, есть ли загруженное изображение для обработки
    if (videoModal.scene?.is_continue && !videoModal.uploadedImage) {
      setError("Please upload an image for the 'continue' scene.");
      return;
    }
    
    try {
      setVideoModal(prev => ({ ...prev, isGenerating: true }));
      
      let imageUrlForGeneration = videoModal.scene?.is_continue ? '' : videoModal.scene?.frame_url;
      
      // ШАГ 1: Если есть загруженный файл, отправляем его на наш бэкенд
      if (videoModal.uploadedImage) {
        console.log("Uploading image to our backend first...");
        const formData = new FormData();
        formData.append('file', videoModal.uploadedImage);

        const uploadResponse = await fetch(`${API_BASE}/upload-image`, {
          method: 'POST',
          body: formData,
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload image to the server.');
        }

        const uploadResult = await uploadResponse.json();
        imageUrlForGeneration = uploadResult.url; // Получаем публичный URL
        console.log("Image uploaded, public URL received:", imageUrlForGeneration);
      }

      if (!imageUrlForGeneration) {
        throw new Error("No image URL available for video generation.");
      }
      
      // ШАГ 2: Запускаем генерацию видео с публичным URL
      const requestBody: any = {
        provider: provider,
        image_url: imageUrlForGeneration // Используем новый, публичный URL
      };
      
      const response = await fetch(`${API_BASE}/projects/${selectedProject?.id}/scenes/${sceneId}/generate-video`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to start video generation');
      }

      showSuccess(`${provider} video generation started!`);
      
      const pollKey = `${sceneId}:${provider}`;
      setVideoPolling(prev => new Set([...Array.from(prev), pollKey]));
      
      pollVideoStatus(sceneId, provider);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate video');
      // Устанавливаем isGenerating в false только в случае ошибки. 
      // При успехе он останется true, пока поллинг не завершится.
      setVideoModal(prev => ({ ...prev, isGenerating: false }));
    }
  };

  const pollVideoStatus = async (sceneId: number, provider: string) => {
    try {
      const response = await fetch(`${API_BASE}/projects/${selectedProject?.id}/scenes/${sceneId}/videos/${provider}/status`);
      if (response.ok) {
        const data = await response.json();
        
        if (data.status === 'completed') {
          // Stop polling for this video
          const pollKey = `${sceneId}:${provider}`;
          setVideoPolling(prev => {
            const newSet = new Set(prev);
            newSet.delete(pollKey);
            return newSet;
          });
          
          // Refresh videos in modal if it's open for this scene
          if (videoModal.isOpen && videoModal.scene?.id === sceneId) {
            await refreshVideoModal(sceneId);
          }
          
          setVideoModal(prev => ({ ...prev, isGenerating: false }));
          
          showSuccess(`${provider} video generation completed!`);
        } else if (data.status === 'error') {
          const pollKey = `${sceneId}:${provider}`;
          setVideoPolling(prev => {
            const newSet = new Set(prev);
            newSet.delete(pollKey);
            return newSet;
          });
          setError(`${provider} video generation failed: ${data.error_message || 'Unknown error'}`);
          setVideoModal(prev => ({ ...prev, isGenerating: false }));
        } else {
          // Continue polling
          setTimeout(() => pollVideoStatus(sceneId, provider), 3000);
        }
      }
    } catch (err) {
      console.error('Error polling video status:', err);
      setTimeout(() => pollVideoStatus(sceneId, provider), 5000);
    }
  };

  // Audio generation functions
  const generateAudio = async (sceneId: number, videoProvider: 'seedance' | 'minimax', textPrompt: string) => {
    try {
      setAudioModal(prev => ({ ...prev, isGenerating: true }));
      
      const response = await fetch(`${API_BASE}/projects/${selectedProject?.id}/scenes/${sceneId}/generate-audio`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          video_provider: videoProvider,
          text_prompt: textPrompt
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to start audio generation');
      }

      showSuccess(`Audio generation started from ${videoProvider} video!`);
      
      // Start polling for this audio
      const pollKey = `${sceneId}:${videoProvider}`;
      setAudioPolling(prev => new Set([...Array.from(prev), pollKey]));
      
      // Start polling immediately
      pollAudioStatus(sceneId, videoProvider);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate audio');
      setAudioModal(prev => ({ ...prev, isGenerating: false }));
    }
  };

  const pollAudioStatus = async (sceneId: number, provider: string) => {
    try {
      const response = await fetch(`${API_BASE}/projects/${selectedProject?.id}/scenes/${sceneId}/audio/${provider}/status`);
      if (response.ok) {
        const data = await response.json();
        
        if (data.status === 'completed') {
          // Stop polling for this audio
          const pollKey = `${sceneId}:${provider}`;
          setAudioPolling(prev => {
            const newSet = new Set(prev);
            newSet.delete(pollKey);
            return newSet;
          });
          
          // Refresh audio records in modal if it's open for this scene
          if (audioModal.isOpen && audioModal.scene?.id === sceneId) {
            await refreshAudioModal(sceneId);
          }
          
          setAudioModal(prev => ({ ...prev, isGenerating: false }));
          
          showSuccess(`Audio generation completed from ${provider} video!`);
        } else if (data.status === 'error') {
          const pollKey = `${sceneId}:${provider}`;
          setAudioPolling(prev => {
            const newSet = new Set(prev);
            newSet.delete(pollKey);
            return newSet;
          });
          setError(`Audio generation failed: ${data.error_message || 'Unknown error'}`);
          setAudioModal(prev => ({ ...prev, isGenerating: false }));
        } else {
          // Continue polling
          setTimeout(() => pollAudioStatus(sceneId, provider), 3000);
        }
      }
    } catch (err) {
      console.error('Error polling audio status:', err);
      setTimeout(() => pollAudioStatus(sceneId, provider), 5000);
    }
  };

  // Global video functions
  const uploadGlobalVideo = async () => {
    if (!globalVideoModal.uploadFile) return;
    
    try {
      setGlobalVideoModal(prev => ({ ...prev, isUploading: true }));
      
      const formData = new FormData();
      formData.append('file', globalVideoModal.uploadFile);
      formData.append('text_prompt', globalVideoModal.uploadTextPrompt);
      
      const response = await fetch(`${API_BASE}/global-videos/upload`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to upload video');
      }

      const result = await response.json();
      showSuccess('Video uploaded and processing started in background!');
      
      // Start polling for this global video
      setGlobalVideoPolling(prev => new Set([...Array.from(prev), result.id]));
      pollGlobalVideoStatus(result.id);
      
      // Reset upload form
      setGlobalVideoModal(prev => ({
        ...prev,
        uploadFile: null,
        uploadTextPrompt: '',
        isUploading: false,
        activeTab: 'list'
      }));
      
      // Refresh list
      fetchGlobalVideos();
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload video');
      setGlobalVideoModal(prev => ({ ...prev, isUploading: false }));
    }
  };

  const pollGlobalVideoStatus = async (videoId: number) => {
    try {
      const response = await fetch(`${API_BASE}/global-videos/${videoId}/status`);
      if (response.ok) {
        const data = await response.json();
        
        // Update the video in the list
        setGlobalVideoModal(prev => ({
          ...prev,
          globalVideos: prev.globalVideos.map(video => 
            video.id === videoId ? { ...video, ...data } : video
          )
        }));
        
        if (data.status === 'completed') {
          // Stop polling for this video
          setGlobalVideoPolling(prev => {
            const newSet = new Set(prev);
            newSet.delete(videoId);
            return newSet;
          });
          
          showSuccess('Global video processing completed!');
        } else if (data.status === 'error') {
          setGlobalVideoPolling(prev => {
            const newSet = new Set(prev);
            newSet.delete(videoId);
            return newSet;
          });
          setError(`Global video processing failed: ${data.error_message || 'Unknown error'}`);
        } else {
          // Continue polling
          setTimeout(() => pollGlobalVideoStatus(videoId), 1000); // Poll every second
        }
      }
    } catch (err) {
      console.error('Error polling global video status:', err);
      setTimeout(() => pollGlobalVideoStatus(videoId), 3000);
    }
  };

  const deleteGlobalVideo = async (videoId: number) => {
    try {
      const response = await fetch(`${API_BASE}/global-videos/${videoId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete video');
      }

      showSuccess('Video deleted successfully');
      fetchGlobalVideos(); // Refresh list
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete video');
    }
  };

  // Polling for scene regeneration
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef<boolean>(false);

  const stopPolling = useCallback(() => {
    console.log('⏹️ Stopping polling');
    isPollingRef.current = false;
    setActivePolling(false);
    setQuickStatus(null);
    setProgressStatus(null);
    
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const startPolling = useCallback((projectId: number) => {
    console.log('🔄 Starting status polling for project', projectId);

    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    setActivePolling(true);
    isPollingRef.current = true;

    const pollStatus = async () => {
      if (!isPollingRef.current) return;

      try {
        console.log('📡 Polling status for project', projectId);

        const quickResponse = await fetch(`${API_BASE}/projects/${projectId}/status`);
        if (quickResponse.ok) {
          const quickData: QuickStatus = await quickResponse.json();
          setQuickStatus(quickData);
          console.log('📊 Status:', quickData.status, '-', quickData.current_step);
        }

        const progressResponse = await fetch(`${API_BASE}/projects/${projectId}/progress`);
        if (progressResponse.ok) {
          const progressData: ProgressStatus = await progressResponse.json();
          setProgressStatus(progressData);
          console.log(`📈 Progress: ${progressData.progress_percentage}% - Phase: ${progressData.current_phase}`);

          if (progressData.current_phase === 'resources' && progressData.progress_percentage >= 100) {
            stopPolling();
            showSuccess('All resources generated successfully!');
            await fetchProjectDetails(projectId);
            setViewMode('storyboard');
            return;
          }
        }
      } catch (err) {
        console.error('🚨 Polling error:', err);
      }
    };

    pollStatus();
    pollingRef.current = setInterval(pollStatus, 2000);
  }, [fetchProjectDetails, stopPolling]);

  // Create project
  const createProject = async () => {
    if (!newTitle.trim() || !newDescription.trim()) return;

    setLoading(true);
    try {
      console.log('🚀 Creating project...');
      
      const createResponse = await fetch(`${API_BASE}/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: newTitle,
          description: newDescription,
        }),
      });

      if (!createResponse.ok) throw new Error('Failed to create project');
      
      const newProject = await createResponse.json();
      console.log('✅ Project created:', newProject);
      
      setSelectedProject(newProject);
      setViewMode('upload-scenario');
      setNewTitle('');
      setNewDescription('');
      setError(null);
      showSuccess('Project created! Now upload your JSON scenario.');
      
    } catch (err) {
      console.error('❌ Error creating project:', err);
      setError(err instanceof Error ? err.message : 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  // Upload JSON scenario
  const uploadScenario = async () => {
    if (!selectedProject || !scenarioJson.trim()) return;

    setLoading(true);
    try {
      console.log('📤 Uploading scenario...');
      
      // Parse JSON first to validate
      const parsedScenario = JSON.parse(scenarioJson);
      
      const uploadResponse = await fetch(`${API_BASE}/projects/${selectedProject.id}/upload-scenario`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          scenario_json: parsedScenario
        }),
      });

      if (!uploadResponse.ok) throw new Error('Failed to upload scenario');
      
      const uploadData = await uploadResponse.json();
      console.log('✅ Scenario uploaded:', uploadData);
      
      setViewMode('generating');
      setScenarioJson('');
      setError(null);
      showSuccess('Scenario uploaded! You can now generate resources.');
      
      // Start polling for status
      startPolling(selectedProject.id);
      
    } catch (err) {
      console.error('❌ Error uploading scenario:', err);
      if (err instanceof SyntaxError) {
        setError('Invalid JSON format. Please check your syntax.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to upload scenario');
      }
    } finally {
      setLoading(false);
    }
  };

  // Delete project
  const deleteProject = async (projectId: number) => {
    if (!window.confirm('Delete this project?')) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/projects/${projectId}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete project');
      
      setProjects(prev => prev.filter(p => p.id !== projectId));
      setError(null);
      showSuccess('Project deleted successfully.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete project');
    } finally {
      setLoading(false);
    }
  };

  // View project - goes directly to storyboard
  const viewProject = async (project: Project) => {
    console.log('👁️ Viewing project:', project);
    setSelectedProject(project);
    
    if (project.status === 'structure_complete') {
      await fetchProjectDetails(project.id);
      setViewMode('storyboard');
    } else if (project.status === 'processing') {
      setViewMode('generating');
      console.log('🔄 Project is processing, starting polling...');
      startPolling(project.id);
    } else {
      setViewMode('storyboard');
    }
  };

  // Check auth on mount
  useEffect(() => {
    if (checkAuthCookie()) {
      fetchProjects();
    }
  }, [fetchProjects]);

  // Load projects when authenticated
  useEffect(() => {
    if (isAuthenticated && viewMode === 'list') {
      fetchProjects();
    }
  }, [isAuthenticated, viewMode, fetchProjects]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      console.log('🧹 Component unmounting, stopping polling');
      stopPolling();
    };
  }, [stopPolling]);

  // Auto-refresh modals when they're open
  useEffect(() => {
    let videoRefreshInterval: NodeJS.Timeout | null = null;
    let audioRefreshInterval: NodeJS.Timeout | null = null;

    // Auto-refresh video modal every 5 seconds if open
    if (videoModal.isOpen && videoModal.scene) {
      videoRefreshInterval = setInterval(() => {
        refreshVideoModal(videoModal.scene!.id);
      }, 5000);
    }

    // Auto-refresh audio modal every 5 seconds if open  
    if (audioModal.isOpen && audioModal.scene) {
      audioRefreshInterval = setInterval(() => {
        refreshAudioModal(audioModal.scene!.id);
      }, 5000);
    }

    return () => {
      if (videoRefreshInterval) clearInterval(videoRefreshInterval);
      if (audioRefreshInterval) clearInterval(audioRefreshInterval);
    };
  }, [videoModal.isOpen, videoModal.scene?.id, audioModal.isOpen, audioModal.scene?.id]);

  // Global video status polling effect
  useEffect(() => {
    let globalVideoRefreshInterval: NodeJS.Timeout | null = null;

    // Auto-refresh global videos if modal is open
    if (globalVideoModal.isOpen) {
      globalVideoRefreshInterval = setInterval(() => {
        fetchGlobalVideos();
      }, 1000); // Update every second
    }

    return () => {
      if (globalVideoRefreshInterval) clearInterval(globalVideoRefreshInterval);
    };
  }, [globalVideoModal.isOpen]);

  // Clear success message when error appears
  useEffect(() => {
    if (error) {
      setSuccessMessage(null);
    }
  }, [error]);

  // Helper functions
  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Ready';
      case 'processing': return 'Processing...';
      case 'structure_complete': return 'Ready for Resources';
      case 'completed': return 'Completed';
      case 'error': return 'Error';
      case 'generating': return 'Generating...';
      case 'continue': return 'Continue Scene';
      default: return status;
    }
  };

  // Calculate image height based on window height with 10% margins
  const getImageHeight = () => {
    const windowHeight = window.innerHeight;
    const availableHeight = windowHeight * 0.8; // 80% of window height (10% margin top and bottom)
    return Math.min(availableHeight, 800); // Max 800px
  };

  // Helper function to get video by provider
  const getVideoByProvider = (videos: SceneVideo[], provider: string) => {
    return videos.find(v => v.provider === provider && v.status === 'completed');
  };

  // Helper function to get audio by video provider
  const getAudioByVideoProvider = (audioRecords: SceneAudio[], videoProvider: string) => {
    return audioRecords.find(a => a.video_provider === videoProvider && a.status === 'completed');
  };

  // Render scene card for storyboard (with video and audio buttons)
  const renderStoryboardSceneCard = (scene: Scene) => {
    const character = storyboardData?.characters.find(c => c.id === scene.character_id);
    const environment = storyboardData?.environments.find(e => e.id === scene.environment_id);
    const imageHeight = getImageHeight() * 0.6; // Smaller for scene cards

    return (
      <div key={scene.id} className="storyboard-scene-card">
        <div className="scene-header">
          <h4>Scene {scene.scene_number}</h4>
          <div className="scene-badges">
            {scene.is_continue && (
              <span className="continue-badge">Continue</span>
            )}
          </div>
        </div>

        <div className="scene-frame" style={{ height: `${imageHeight}px` }}>
          {scene.is_continue ? (
            <div className="continue-frame">
              <div className="continue-text">ИСПОЛЬЗУЙ ПОСЛЕДНИЙ КАДР</div>
            </div>
          ) : scene.frame_url ? (
            <img
              src={scene.frame_url}
              alt={`Scene ${scene.scene_number}`}
              onClick={() => openModal(scene.frame_url!, `Scene ${scene.scene_number}`, 'Storyboard Frame')}
              className="scene-frame-img clickable"
            />
          ) : (
            <div className="scene-frame-placeholder">
              🎬
            </div>
          )}
        </div>

        <div className="scene-description">
          <p>{scene.description}</p>
          <div className="scene-meta">
            {character && <small><strong>Character:</strong> {character.name}</small>}
            {environment && <small><strong>Environment:</strong> {environment.name}</small>}
            <small><strong>First Frame:</strong> {scene.first_frame_description}</small>
            {scene.character_variation && scene.character_variation !== 'default' && (
              <small><strong>Variation:</strong> {scene.character_variation}</small>
            )}
          </div>
        </div>

        <div className="scene-actions">
          <button
            onClick={() => openSceneModal(scene)}
            className="btn btn-sm btn-success"
          >
            Details
          </button>

          <button
            onClick={() => openImageEditModal(scene)}
            className="btn btn-sm btn-warning"
            disabled={!scene.frame_url}
            title={!scene.frame_url ? "Generate frame first" : "Edit Scene Image"}
          >
            ✏️ Edit Image
          </button>

          <button
            onClick={() => openVideoModal(scene)}
            className="btn btn-sm btn-primary"
            // For continue scenes, always enable video button
            disabled={!scene.is_continue && !scene.frame_url}
            title={(!scene.is_continue && !scene.frame_url) ? "Generate frame first" : "Generate video"}
          >
            🎥 Video
            {(videoPolling.has(`${scene.id}:seedance`) || videoPolling.has(`${scene.id}:minimax`)) && (
              <span className="generating-indicator">⏳</span>
            )}
          </button>

         <button
            onClick={() => openAudioModal(scene)}
            className="btn btn-sm btn-warning"
          >
            🔊 Audio
            {(audioPolling.has(`${scene.id}:seedance`) || audioPolling.has(`${scene.id}:minimax`)) && (
              <span className="generating-indicator">⏳</span>
            )}
          </button> 

          <button
            onClick={() => openVEO3Modal(scene)}
            className="btn btn-sm btn-secondary veo3-btn"
          >
            VEO3
          </button>
        </div>
      </div>
    );
  };

  // Loading overlay component
  const LoadingOverlay = ({ isVisible, message = 'Loading...' }: { isVisible: boolean; message?: string }) => (
    isVisible ? (
      <div className="loading-overlay-fullscreen">
        <div className="loading-content">
          <div className="loading-spinner" />
          <div className="loading-message">{message}</div>
        </div>
      </div>
    ) : null
  );

  // Render login form
  const renderLogin = () => (
    <div className="app">
      <div className="container">
        <div className="login-form">
          <h1>Login to Anime Projects</h1>
          
          <div className="form-group">
            <label>Username</label>
            <input
              type="text"
              value={loginForm.username}
              onChange={(e) => setLoginForm(prev => ({ ...prev, username: e.target.value }))}
              placeholder="Enter username"
              onKeyPress={(e) => e.key === 'Enter' && login()}
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={loginForm.password}
              onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
              placeholder="Enter password"
              onKeyPress={(e) => e.key === 'Enter' && login()}
            />
          </div>

          {authError && <div className="error-message">{authError}</div>}

          <div className="form-actions">
            <button
              onClick={login}
              disabled={!loginForm.username.trim() || !loginForm.password.trim()}
              className="btn btn-primary btn-lg"
            >
              Login
            </button>
          </div>

          
        </div>
      </div>
    </div>
  );

  // Render modal
  const renderModal = () => {
    if (!modal.isOpen) return null;

    return (
      <div className="modal-overlay image-modal-overlay" onClick={closeModal}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>{modal.title} - {modal.subtitle}</h3>
            <button onClick={closeModal} className="modal-close">✕</button>
          </div>
          <div className="modal-body">
            <img src={modal.imageUrl} alt={`${modal.title} ${modal.subtitle}`} className="modal-image" />
          </div>
        </div>
      </div>
    );
  };

  // Render scene modal
  const renderSceneModal = () => {
    if (!sceneModal.isOpen || !sceneModal.scene) return null;

    const scene = sceneModal.scene;
    const character = storyboardData?.characters.find(c => c.id === scene.character_id);
    const environment = storyboardData?.environments.find(e => e.id === scene.environment_id);

    return (
      <div className="modal-overlay scene-modal-overlay" onClick={closeSceneModal}>
        <div className="modal-content scene-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Scene {scene.scene_number} - Episode {scene.episode_number}</h3>
            <button onClick={closeSceneModal} className="modal-close">✕</button>
          </div>
          <div className="modal-body">
            <div className="scene-content">
              <h4>Description</h4>
              <p>{scene.description}</p>
              
              <h4>Details</h4>
              <p><strong>Character:</strong> {character?.name}</p>
              <p><strong>Environment:</strong> {environment?.name}</p>
              <p><strong>First Frame:</strong> {scene.first_frame_description}</p>
              <p><strong>Is Continue:</strong> {scene.is_continue ? 'Yes' : 'No'}</p>
              {scene.character_variation && scene.character_variation !== 'default' && (
                <p><strong>Character Variation:</strong> {scene.character_variation}</p>
              )}
              
              <h4>VEO3 Prompt</h4>
              <div className="prompt-display">
                {scene.veo3_prompt}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render VEO3 modal
  const renderVEO3Modal = () => {
    if (!veo3Modal.isOpen || !veo3Modal.scene) return null;

    const scene = veo3Modal.scene;

    return (
      <div className="modal-overlay veo3-prompt-modal-overlay" onClick={closeVEO3Modal}>
        <div className="modal-content veo3-prompt-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>VEO3 Video Prompt - Scene {scene.scene_number}</h3>
            <button onClick={closeVEO3Modal} className="modal-close">✕</button>
          </div>
          <div className="modal-body">
            <div className="composition-section">
              <h4>VEO3 Video Generation Prompt:</h4>
              
              <div className="prompt-display">
                {scene.veo3_prompt || 'VEO3 prompt not available for this scene'}
              </div>
              
              <div style={{ marginTop: '1rem' }}>
                <h5>About VEO3 Prompts:</h5>
                <p style={{ fontSize: '0.9rem', color: '#6b7280', lineHeight: '1.5' }}>
                  VEO3 prompts are specialized instructions for video generation from the storyboard frames. 
                  They include specific timing, camera instructions, audio requirements, and scene composition 
                  details optimized for VEO3's video synthesis capabilities. All camera movements are static 
                  (locked tripod) for consistent animation style.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render video generation modal
  const renderVideoModal = () => {
    if (!videoModal.isOpen || !videoModal.scene) return null;

    const scene = videoModal.scene;
    const seedanceVideo = getVideoByProvider(videoModal.videos, 'seedance');
    const minimaxVideo = getVideoByProvider(videoModal.videos, 'minimax');

    return (
      <div className="modal-overlay" onClick={closeVideoModal}>
        <div className="modal-content video-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>🎥 Video Generation - Scene {scene.scene_number}</h3>
            <button onClick={closeVideoModal} className="modal-close">✕</button>
          </div>
          <div className="modal-body">
            
            {/* Image upload for continue scenes */}
            {scene.is_continue && (
              <div className="image-upload-section">
                <h4>Upload Image for Continue Scene:</h4>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="image-upload-input"
                />
                {videoModal.uploadedImageUrl && (
                  <div className="uploaded-image-preview">
                    <img src={videoModal.uploadedImageUrl} alt="Uploaded" className="uploaded-image" />
                  </div>
                )}
              </div>
            )}

            {/* Provider Selection */}
            <div className="provider-selection">
              <h4>Select Video Provider:</h4>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    value="seedance"
                    checked={videoModal.selectedProvider === 'seedance'}
                    onChange={(e) => setVideoModal(prev => ({ ...prev, selectedProvider: e.target.value as 'seedance' | 'minimax' }))}
                  />
                  <span>Seedance (ByteDance)</span>
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    value="minimax"
                    checked={videoModal.selectedProvider === 'minimax'}
                    onChange={(e) => setVideoModal(prev => ({ ...prev, selectedProvider: e.target.value as 'seedance' | 'minimax' }))}
                  />
                  <span>Minimax Hailuo-02</span>
                </label>
              </div>
            </div>

            {/* Generate Button */}
            <div className="generate-section">
              <button
                onClick={() => generateVideo(scene.id, videoModal.selectedProvider)}
                disabled={videoModal.isGenerating || (scene.is_continue && !videoModal.uploadedImageUrl) || (!scene.is_continue && !scene.frame_url)}
                className="btn btn-primary btn-full"
              >
                {videoModal.isGenerating ? 'Generating...' : `Generate ${videoModal.selectedProvider} Video`}
              </button>
              {scene.is_continue && !videoModal.uploadedImageUrl && (
                <p className="error-text">
                  Continue scene requires image upload.
                </p>
              )}
              {!scene.is_continue && !scene.frame_url && (
                <p className="error-text">
                  Scene frame required. Generate frame first.
                </p>
              )}
              
              {/* Refresh Status Button */}
              <button
                onClick={() => refreshVideoModal(scene.id)}
                className="btn btn-sm btn-secondary"
                style={{ marginTop: '0.5rem' }}
              >
                🔄 Refresh Status
              </button>
            </div>

            {/* Video Results */}
            <div className="video-results">
              <h4>Generated Videos:</h4>
              
              {/* Seedance Video */}
              <div className="video-result-card">
                <h5>🎬 Seedance Video</h5>
                {seedanceVideo ? (
                  <div>
                    <video
                      src={seedanceVideo.video_url}
                      controls
                      className="video-player"
                    />
                    <div className="video-actions">
                      <span className="status-badge status-completed">✅ Ready</span>
                      <a
                        href={seedanceVideo.video_url}
                        download={`scene_${scene.scene_number}_seedance.mp4`}
                        className="btn btn-sm btn-secondary"
                      >
                        Download
                      </a>
                      {seedanceVideo.is_from_uploaded_image && (
                        <span className="upload-badge">📤 From Upload</span>
                      )}
                    </div>
                  </div>
                ) : videoPolling.has(`${scene.id}:seedance`) ? (
                  <div className="loading-state">
                    <div className="loading-spinner" />
                    <p>Generating Seedance video...</p>
                  </div>
                ) : (
                  <p className="no-video-text">No Seedance video generated yet</p>
                )}
              </div>

              {/* Minimax Video */}
              <div className="video-result-card">
                <h5>🎭 Minimax Video</h5>
                {minimaxVideo ? (
                  <div>
                    <video
                      src={minimaxVideo.video_url}
                      controls
                      className="video-player"
                    />
                    <div className="video-actions">
                      <span className="status-badge status-completed">✅ Ready</span>
                      <a
                        href={minimaxVideo.video_url}
                        download={`scene_${scene.scene_number}_minimax.mp4`}
                        className="btn btn-sm btn-secondary"
                      >
                        Download
                      </a>
                      {minimaxVideo.is_from_uploaded_image && (
                        <span className="upload-badge">📤 From Upload</span>
                      )}
                    </div>
                  </div>
                ) : videoPolling.has(`${scene.id}:minimax`) ? (
                  <div className="loading-state">
                    <div className="loading-spinner" />
                    <p>Generating Minimax video...</p>
                  </div>
                ) : (
                  <p className="no-video-text">No Minimax video generated yet</p>
                )}
              </div>
            </div>

            {/* Prompt Info */}
            <div className="prompt-info">
              <h5>Video Prompt:</h5>
              <p className="prompt-text">
                {scene.veo3_prompt || scene.description}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderImageEditModal = () => {
    if (!imageEditModal.isOpen || !imageEditModal.scene) return null;

    const { scene, selectedModel, prompt, edits, isGenerating, pollingEdits } = imageEditModal;

    return (
      <div className="modal-overlay" onClick={closeImageEditModal}>
        <div className="modal-content image-edit-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>✏️ Edit Image - Scene {scene.scene_number}</h3>
            <button onClick={closeImageEditModal} className="modal-close">✕</button>
          </div>
          <div className="modal-body">
            
            {/* Секция нового редактирования */}
            <div className="new-edit-section">
              <h4>New Edit</h4>
              <div className="original-image-container">
                <p>Original Image:</p>
                <img src={scene.frame_url} alt="Original Frame" className="original-image-preview"/>
              </div>
              <div className="form-group">
                <label>Select Model:</label>
                <select
                  value={selectedModel}
                  onChange={(e) => setImageEditModal(prev => ({ ...prev, selectedModel: e.target.value as any }))}
                  className="form-group select"
                >
                  <option value="nano-banana">Nano Banana</option>
                  <option value="flux-pro">Flux-pro</option>
                  <option value="qwen">Qwen</option>
                </select>
              </div>
              <div className="form-group">
                <label>Prompt:</label>
                <textarea
                  value={prompt}
                  onChange={(e) => setImageEditModal(prev => ({ ...prev, prompt: e.target.value }))}
                  placeholder="e.g., make her smile, add rain in the background"
                  rows={3}
                  className="form-group textarea"
                />
              </div>
              <button
                onClick={startImageEdit}
                disabled={isGenerating || !prompt.trim()}
                className="btn btn-primary btn-full"
              >
                {isGenerating ? 'Starting...' : 'Start Editing'}
              </button>
            </div>

            {/* История редактирований */}
            <div className="edit-history">
              <h4>Edit History <button onClick={() => fetchImageEdits(scene.id)} className="btn btn-sm btn-secondary">🔄</button></h4>
              {edits.length === 0 ? (
                <p className="no-edits-text">No edits yet for this scene.</p>
              ) : (
                <div className="edit-history-list">
                  {edits.map(edit => (
                    <div key={edit.id} className="edit-history-item">
                      <div className="edit-item-header">
                        <h5>Model: {edit.model_type}</h5>
                        <span className={`status-badge status-${edit.status}`}>{edit.status}</span>
                      </div>
                      <p className="edit-prompt"><strong>Prompt:</strong> {edit.prompt}</p>
                      
                      <div className="image-previews">
                        <div className="image-preview-box">
                          <h6>Original</h6>
                          <img src={edit.original_image_url} alt="Original"/>
                        </div>
                        <div className="image-preview-box">
                          <h6>Edited</h6>
                          {edit.status === 'completed' && edit.edited_image_url && (
                            <img src={edit.edited_image_url} alt="Edited"/>
                          )}
                          {(edit.status === 'generating' || pollingEdits.has(edit.id)) && (
                            <div className="loading-state">
                              <div className="loading-spinner"/>
                              <p>Generating...</p>
                            </div>
                          )}
                          {edit.status === 'error' && (
                            <div className="error-display">
                              <p>❌ Error</p>
                              <small>{edit.error_message}</small>
                            </div>
                          )}
                           {edit.status === 'pending' && (
                            <div className="pending-display">
                              <p>🕒 Pending</p>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {(edit.status === 'generating' || edit.status === 'pending' || pollingEdits.has(edit.id)) && (
                         <button onClick={() => pollImageEditStatus(scene.id, edit.id)} className="btn btn-sm">
                           Check Status
                         </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    );
  };

  // Render audio generation modal
  const renderAudioModal = () => {
    if (!audioModal.isOpen || !audioModal.scene) return null;

    const scene = audioModal.scene;

    return (
      <div className="modal-overlay" onClick={closeAudioModal}>
        <div className="modal-content audio-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>🔊 Audio Generation - Scene {scene.scene_number}</h3>
            <button onClick={closeAudioModal} className="modal-close">✕</button>
          </div>
          <div className="modal-body">
            {/* Video Provider Selection */}
            <div className="provider-selection">
              <h4>Select Video for Audio Generation:</h4>
              
              {/* Show available videos */}
              <div className="available-videos">
                <div className="video-option">
                  <label className="radio-label">
                    <input
                      type="radio"
                      value="seedance"
                      checked={audioModal.selectedVideoProvider === 'seedance'}
                      onChange={(e) => setAudioModal(prev => ({ ...prev, selectedVideoProvider: e.target.value as 'seedance' | 'minimax' }))}
                    />
                    <span>Seedance Video</span>
                    {(() => {
                      const seedanceVideo = getVideoByProvider(videoModal.videos, 'seedance');
                      return seedanceVideo ? (
                        <span className="video-status-ok">✅ Available</span>
                      ) : (
                        <span className="video-status-missing">❌ Not generated</span>
                      );
                    })()}
                  </label>
                </div>
                
                <div className="video-option">
                  <label className="radio-label">
                    <input
                      type="radio"
                      value="minimax"
                      checked={audioModal.selectedVideoProvider === 'minimax'}
                      onChange={(e) => setAudioModal(prev => ({ ...prev, selectedVideoProvider: e.target.value as 'seedance' | 'minimax' }))}
                    />
                    <span>Minimax Video</span>
                    {(() => {
                      const minimaxVideo = getVideoByProvider(videoModal.videos, 'minimax');
                      return minimaxVideo ? (
                        <span className="video-status-ok">✅ Available</span>
                      ) : (
                        <span className="video-status-missing">❌ Not generated</span>
                      );
                    })()}
                  </label>
                </div>
              </div>
            </div>

            {/* Text Prompt */}
            <div className="text-prompt-section">
              <label>Additional Audio Description (optional):</label>
              <textarea
                value={audioModal.textPrompt}
                onChange={(e) => setAudioModal(prev => ({ ...prev, textPrompt: e.target.value }))}
                placeholder="Optional: Describe additional sounds or audio effects..."
                rows={2}
                className="text-prompt-input"
              />
              <small style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                Leave empty for automatic audio generation from video content only
              </small>
            </div>

            {/* Check if selected video exists and generate button */}
            {(() => {
              const selectedVideo = getVideoByProvider(videoModal.videos, audioModal.selectedVideoProvider);
              
              return (
                <div className="generate-section">
                  {!selectedVideo ? (
                    <div className="error-section">
                      <p className="error-text">
                        ❌ No {audioModal.selectedVideoProvider} video found. Generate the {audioModal.selectedVideoProvider} video first.
                      </p>
                    </div>
                  ) : (
                    <button
                      onClick={() => generateAudio(scene.id, audioModal.selectedVideoProvider, audioModal.textPrompt)}
                      disabled={audioModal.isGenerating}
                      className="btn btn-warning btn-full"
                    >
                      {audioModal.isGenerating ? 'Generating Audio...' : `Generate Audio from ${audioModal.selectedVideoProvider} Video`}
                    </button>
                  )}
                  
                  {/* Refresh Status Button */}
                  <button
                    onClick={() => refreshAudioModal(scene.id)}
                    className="btn btn-sm btn-secondary"
                    style={{ marginTop: '0.5rem' }}
                  >
                    🔄 Refresh Status
                  </button>
                </div>
              );
            })()}

            {/* Audio Results */}
            <div className="audio-results">
              <h4>Generated Audio Tracks:</h4>
              
              {/* Seedance Audio */}
              <div className="audio-result-card">
                <h5>🎬 From Seedance Video</h5>
                {(() => {
                  const seedanceAudio = getAudioByVideoProvider(audioModal.audioRecords, 'seedance');
                  if (seedanceAudio) {
                    return (
                      <div>
                        <audio
                          src={seedanceAudio.audio_url}
                          controls
                          className="audio-player"
                        />
                        <div className="audio-actions">
                          <span className="status-badge status-completed">✅ Ready</span>
                          <a
                            href={seedanceAudio.audio_url}
                            download={`scene_${scene.scene_number}_seedance_audio.wav`}
                            className="btn btn-sm btn-secondary"
                          >
                            Download
                          </a>
                        </div>
                        {seedanceAudio.text_prompt && (
                          <p className="audio-prompt-text">
                            Prompt: {seedanceAudio.text_prompt}
                          </p>
                        )}
                      </div>
                    );
                  } else if (audioPolling.has(`${scene.id}:seedance`)) {
                    return (
                      <div className="loading-state">
                        <div className="loading-spinner" />
                        <p>Generating audio from Seedance video...</p>
                      </div>
                    );
                  } else {
                    return <p className="no-audio-text">No audio generated from Seedance video yet</p>;
                  }
                })()}
              </div>

              {/* Minimax Audio */}
              <div className="audio-result-card">
                <h5>🎭 From Minimax Video</h5>
                {(() => {
                  const minimaxAudio = getAudioByVideoProvider(audioModal.audioRecords, 'minimax');
                  if (minimaxAudio) {
                    return (
                      <div>
                        <audio
                          src={minimaxAudio.audio_url}
                          controls
                          className="audio-player"
                        />
                        <div className="audio-actions">
                          <span className="status-badge status-completed">✅ Ready</span>
                          <a
                            href={minimaxAudio.audio_url}
                            download={`scene_${scene.scene_number}_minimax_audio.wav`}
                            className="btn btn-sm btn-secondary"
                          >
                            Download
                          </a>
                        </div>
                        {minimaxAudio.text_prompt && (
                          <p className="audio-prompt-text">
                            Prompt: {minimaxAudio.text_prompt}
                          </p>
                        )}
                      </div>
                    );
                  } else if (audioPolling.has(`${scene.id}:minimax`)) {
                    return (
                      <div className="loading-state">
                        <div className="loading-spinner" />
                        <p>Generating audio from Minimax video...</p>
                      </div>
                    );
                  } else {
                    return <p className="no-audio-text">No audio generated from Minimax video yet</p>;
                  }
                })()}
              </div>
            </div>

            {/* Info Section */}
            <div className="info-section">
              <h5>About Video to SFX Audio Generation:</h5>
              <p className="info-text">
                AI automatically generates sound effects and ambient audio from your video content. 
                The system analyzes the visual content and creates appropriate audio tracks.
                Additional text prompts are optional and can help guide specific sound effects.
              </p>
              <ul style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '0.5rem', paddingLeft: '1.2rem' }}>
                <li>🎬 Analyzes video visually to create matching audio</li>
                <li>🔊 Generates ambient sounds, effects, and atmosphere</li>
                <li>⚡ Works automatically - no prompts required</li>
                <li>📝 Optional text prompts for specific sounds</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Global Video Modal
  const renderGlobalVideoModal = () => {
    if (!globalVideoModal.isOpen) return null;

    return (
      <div className="modal-overlay" onClick={closeGlobalVideoModal}>
        <div className="modal-content global-video-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>🌍 Global Video to Audio Processing</h3>
            <button onClick={closeGlobalVideoModal} className="modal-close">✕</button>
          </div>
          <div className="modal-body">
            
            {/* Tabs */}
            <div className="tabs">
              <button
                className={`tab ${globalVideoModal.activeTab === 'upload' ? 'active' : ''}`}
                onClick={() => setGlobalVideoModal(prev => ({ ...prev, activeTab: 'upload' }))}
              >
                📤 Upload
              </button>
              <button
                className={`tab ${globalVideoModal.activeTab === 'list' ? 'active' : ''}`}
                onClick={() => setGlobalVideoModal(prev => ({ ...prev, activeTab: 'list' }))}
              >
                📋 Files
              </button>
            </div>

            {/* Upload Tab */}
            {globalVideoModal.activeTab === 'upload' && (
              <div className="tab-content">
                <h4>Upload Video for Audio Processing</h4>
                
                <div className="form-group">
                  <label>Video File:</label>
                  <input
                    type="file"
                    accept="video/*"
                    onChange={(e) => setGlobalVideoModal(prev => ({ 
                      ...prev, 
                      uploadFile: e.target.files?.[0] || null 
                    }))}
                    className="file-upload-input"
                  />
                </div>

                <div className="form-group">
                  <label>Additional Audio Description (optional):</label>
                  <textarea
                    value={globalVideoModal.uploadTextPrompt}
                    onChange={(e) => setGlobalVideoModal(prev => ({ 
                      ...prev, 
                      uploadTextPrompt: e.target.value 
                    }))}
                    placeholder="Optional: Describe desired audio effects or sounds..."
                    rows={3}
                    className="text-prompt-input"
                  />
                </div>

                <button
                  onClick={uploadGlobalVideo}
                  disabled={!globalVideoModal.uploadFile || globalVideoModal.isUploading}
                  className="btn btn-primary btn-full"
                >
                  {globalVideoModal.isUploading ? 'Uploading...' : 'Upload & Process'}
                </button>

                <div className="info-section" style={{ marginTop: '1rem' }}>
                  <p className="info-text">
                    Upload any video file to extract audio using AI. The system will analyze your video 
                    and generate matching sound effects and ambient audio automatically.
                  </p>
                </div>
              </div>
            )}

            {/* List Tab */}
            {globalVideoModal.activeTab === 'list' && (
              <div className="tab-content">
                <div className="files-header">
                  <h4>Uploaded Files</h4>
                  <button
                    onClick={fetchGlobalVideos}
                    className="btn btn-sm btn-secondary"
                  >
                    🔄 Refresh
                  </button>
                </div>

                <div className="global-videos-list">
                  {globalVideoModal.globalVideos.length === 0 ? (
                    <p className="no-files-text">No files uploaded yet</p>
                  ) : (
                    globalVideoModal.globalVideos.map((video) => (
                      <div key={video.id} className="global-video-item">
                        <div className="video-info">
                          <h5>{video.filename}</h5>
                          <div className="video-status">
                            <span className={`status-badge status-${video.status}`}>
                              {video.status === 'pending' && '⏳ Pending'}
                              {video.status === 'processing' && '🔄 Processing'}
                              {video.status === 'completed' && '✅ Completed'}
                              {video.status === 'error' && '❌ Error'}
                            </span>
                            {globalVideoPolling.has(video.id) && (
                              <span className="generating-indicator">⏳</span>
                            )}
                          </div>
                          {video.text_prompt && (
                            <p className="video-prompt">Prompt: {video.text_prompt}</p>
                          )}
                          {video.error_message && (
                            <p className="error-text">{video.error_message}</p>
                          )}
                        </div>

                        <div className="video-actions">
                          {video.processed_audio_url && (
                            <>
                              <audio
                                src={video.processed_audio_url}
                                controls
                                className="audio-player-compact"
                              />
                              <a
                                href={video.processed_audio_url}
                                download={`${video.filename}_audio.wav`}
                                className="btn btn-sm btn-success"
                              >
                                Download
                              </a>
                            </>
                          )}
                          <button
                            onClick={() => deleteGlobalVideo(video.id)}
                            className="btn btn-sm btn-danger"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    );
  };

  // Render floating video widget
  const renderFloatingVideoWidget = () => {
    if (!isAuthenticated) return null;

    return (
      <div className="floating-video-widget">
        <button
          onClick={openGlobalVideoModal}
          className="floating-widget-btn"
          title="Global Video Processing"
        >
          🎬
        </button>
      </div>
    );
  };

  // Render projects list
  const renderProjectsList = () => (
    <div className="app">
      <LoadingOverlay isVisible={loading} message="Loading projects..." />
      
      <div className="container">
        <div className="header">
          <h1>JSON Scenario Anime Projects</h1>
          <div className="header-actions">
            <button onClick={() => setViewMode('create')} className="btn btn-primary">
              Create New Project
            </button>
            <button onClick={logout} className="btn btn-secondary">
              Logout
            </button>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}
        {successMessage && <div className="success-message">{successMessage}</div>}

        <div className="projects-grid">
          {projects.map(project => (
            <div key={project.id} className="project-card">
              <div className="project-content">
                <h3>{project.title}</h3>
                <p className="description">{project.description}</p>
                <div className="project-footer">
                  <span className={`status status-${project.status.replace('_', '-')}`}>
                    {getStatusText(project.status)}
                  </span>
                  <span className="date">{new Date(project.created_at).toLocaleDateString()}</span>
                </div>
                {project.scenario_structure && (
                  <div className="project-stats">
                    <span>Episodes: {project.total_episodes}</span>
                    <span>Characters: {project.total_characters}</span>
                    <span>Scenes: {project.total_scenes}</span>
                  </div>
                )}
              </div>
              <div className="project-actions">
                {(project.status === 'structure_complete' || project.status === 'processing') && (
                  <button onClick={() => viewProject(project)} className="btn btn-secondary">
                    View Storyboard
                  </button>
                )}
                <button onClick={() => deleteProject(project.id)} disabled={loading} className="btn btn-danger">
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>

        {projects.length === 0 && !loading && (
          <div className="empty-state">
            <p>No projects yet. Create a new project to get started!</p>
          </div>
        )}
      </div>
    </div>
  );

  // Render create form
  const renderCreateForm = () => (
    <div className="app">
      <LoadingOverlay isVisible={loading} message="Creating project..." />
      
      <div className="container">
        <div className="create-form">
          <h1>Create New Anime Project</h1>
          
          <div className="ai-info-section">
            <h3>Process:</h3>
            <ul>
              <li>📝 Create project with title and description</li>
              <li>📤 Upload your JSON scenario with character_references, environments, and scenes</li>
              <li>🎭 Generate character reference images and variations</li>
              <li>🏞️ Generate environment backgrounds</li>
              <li>🎬 Generate scene frames from your scenarios</li>
              <li>🎥 Generate videos using Seedance or Minimax</li>
              <li>🔊 Generate audio tracks from videos</li>
            </ul>
          </div>
          
          <div className="form-group">
            <label>Project Title</label>
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Enter project title"
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="Brief description of your anime project"
              rows={3}
            />
          </div>

          {error && <div className="error-message">{error}</div>}
          {successMessage && <div className="success-message">{successMessage}</div>}

          <div className="form-actions">
            <button onClick={() => setViewMode('list')} disabled={loading} className="btn btn-secondary">
              Back
            </button>
            <button
              onClick={createProject}
              disabled={loading || !newTitle.trim() || !newDescription.trim()}
              className="btn btn-primary btn-lg"
            >
              Create Project
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Render scenario upload form
  const renderScenarioUpload = () => {
    const exampleJson = `{
  "character_references": {
    "nika": {
      "reference_prompt": "full body portrait, white background, T-pose with arms extended horizontally, neutral facial expression, moe anime style, 26-year-old woman, long straight chestnut-brown hair in a low ponytail with a few soft flyaways, large warm hazel eyes, oval face with gentle cheekbones and small natural lips, slender build with calm grounded posture, default outfit casual/work mix: dark denim jacket or dark work apron over plain black t-shirt, straight blue jeans, canvas sneakers, carries a slightly worn stamp pad and a key lanyard",
      "variations": {
        "work": "dark work apron over plain black t-shirt, blue jeans, canvas sneakers, hair tied in a low ponytail",
        "crisis": "same work outfit but visibly wrinkled and slightly smudged, apron strap askew, hair a bit messy from stress",
        "casual": "light cardigan over white tee, relaxed-fit jeans, simple sneakers",
        "office": "light blouse tucked into dark trousers, slim belt, low heels",
        "evening": "simple dark dress, minimal silver necklace, soft makeup"
      }
    }
  },
  "environments": {
    "photolab_front_counter": "small analog photo lab reception area, off-white painted walls with pegboard displaying film canisters, matte laminate front counter with a small service bell and neatly stacked kraft envelopes, shelves behind the counter labeled with chemistry bottles and a vintage 35mm camera on a stand, a worn ink stamp pad sits near the lamp, warm overhead fluorescent lighting with a slight flicker plus an amber desk lamp creating a soft pool of light on the counter, color palette of warm creams, muted browns, and desaturated reds, intimate hardworking nostalgic atmosphere, anime background art style with realistic textures that support quiet determination and revelation scenes"
  },
  "episodes": [
    {
      "episode_number": 1,
      "episode_title": "Подпись под светом",
      "scenes": [
        {
          "scene_number": 1,
          "description": "Ника ровно раскладывает конверты: рабочий настрой, без чудес.",
          "character_key": "nika",
          "character_variation": "work",
          "environment_key": "photolab_front_counter",
          "first_frame_prompt": "Nika in work outfit stands behind the counter, both hands tidily aligning kraft envelopes edge-to-edge under amber desk lamp; calm eyes; medium shot; static camera.",
          "is_continue": false,
          "veo3_prompt": "NIKA VOICE ONLY – ANIME GENTLE RESOLVE STYLE: Camera on static tripod, locked position, no movement. Nika carefully arranges envelopes on counter in precise lines, hands moving with practiced efficiency. Soft amber desk lamp creates warm pool of light. Camera captures medium shot of counter workspace with Nika's focused movements. Duration: 5 seconds. Audio: gentle paper rustling, soft footsteps, ambient photo lab sounds, no music, realistic workplace atmosphere."
        }
      ]
    }
  ]
}`;

    return (
      <div className="app">
        <LoadingOverlay isVisible={loading} message="Uploading scenario..." />
        
        <div className="container">
          <div className="create-form">
            <h1>Upload JSON Scenario for "{selectedProject?.title}"</h1>
            
            <div className="ai-info-section">
              <h3>JSON Structure Required:</h3>
              <ul>
                <li><strong>character_references:</strong> Object with character names and reference prompts + variations</li>
                <li><strong>environments:</strong> Object with environment names and reference descriptions</li>
                <li><strong>episodes:</strong> Array of episodes with scenes</li>
                <li><strong>scenes:</strong> Contains scene_number, description, character_key, character_variation, environment_key, first_frame_description, is_continue, veo3_prompt</li>
              </ul>
            </div>

            <div className="form-group">
              <label>JSON Scenario</label>
              <textarea
                value={scenarioJson}
                onChange={(e) => setScenarioJson(e.target.value)}
                placeholder="Paste your JSON scenario here..."
                rows={20}
                className="json-textarea"
              />
            </div>

            <details className="example-details">
              <summary className="example-summary">
                📋 Example JSON Structure
              </summary>
              <pre className="example-json">
                {exampleJson}
              </pre>
            </details>

            {error && <div className="error-message">{error}</div>}
            {successMessage && <div className="success-message">{successMessage}</div>}

            <div className="form-actions">
              <button onClick={() => setViewMode('list')} disabled={loading} className="btn btn-secondary">
                Back
              </button>
              <button
                onClick={uploadScenario}
                disabled={loading || !scenarioJson.trim()}
                className="btn btn-success btn-lg"
              >
                Upload Scenario
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render generation progress
  const renderGenerationProgress = () => {
    return (
      <div className="app">
        <div className="container">
          <div className="progress-container">
            <h1>{selectedProject?.title}</h1>
            
            {quickStatus && (
              <div className="progress-section">
                <h2>{getStatusText(quickStatus.status)}</h2>
                <p>{quickStatus.current_step}</p>
                <small className="last-updated">
                  Last updated: {new Date(quickStatus.timestamp).toLocaleTimeString()}
                </small>
              </div>
            )}

            {progressStatus && (
              <div className="progress-section">
                <h3>Phase: {progressStatus.current_phase}</h3>
                <p>{progressStatus.current_step}</p>
                <p>{progressStatus.completed_steps} / {progressStatus.total_steps} steps completed</p>
                
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${progressStatus.progress_percentage}%` }} />
                </div>
                <span className="progress-text">{progressStatus.progress_percentage}%</span>
              </div>
            )}

            {activePolling && (
              <div className="progress-section">
                <p className="polling-message">
                  🔄 Processing your JSON scenario and generating resources...
                </p>
              </div>
            )}

            <div className="progress-actions">
              <button
                onClick={() => {
                  stopPolling();
                  setViewMode('list');
                }}
                className="btn btn-secondary"
              >
                Back to Projects
              </button>
              <button
                onClick={() => {
                  if (selectedProject) {
                    startPolling(selectedProject.id);
                  }
                }}
                disabled={activePolling}
                className="btn btn-primary"
              >
                {activePolling ? 'Polling Active' : 'Check Status'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render storyboard with horizontal scrolling episodes
  const renderStoryboard = () => {
    if (!storyboardData) return null;

    // Group scenes by episode
    const episodeGroups: { [episodeId: number]: Scene[] } = {};
    storyboardData.scenes?.forEach(scene => {
      if (!episodeGroups[scene.episode_id]) {
        episodeGroups[scene.episode_id] = [];
      }
      episodeGroups[scene.episode_id].push(scene);
    });

    return (
      <div className="app">
        <LoadingOverlay isVisible={loading} message="Loading storyboard..." />
        
        <div className="container">
          <div className="storyboard-view">
            <div className="storyboard-header">
              <div>
                <h1>Storyboard: {selectedProject?.title}</h1>
                <p>{selectedProject?.description}</p>
              </div>
              <div className="header-actions">
                <button 
                  onClick={() => {
                    if (selectedProject) {
                      fetchProjectDetails(selectedProject.id);
                      showSuccess('Storyboard refreshed!');
                    }
                  }} 
                  className="btn btn-sm btn-secondary"
                >
                  🔄 Refresh All
                </button>
                <button onClick={() => setViewMode('list')} className="btn btn-secondary">
                  Back to Projects
                </button>
                <button onClick={logout} className="btn btn-secondary">
                  Logout
                </button>
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}
            {successMessage && <div className="success-message">{successMessage}</div>}

            {/* Episodes with horizontal scrolling scenes */}
            <div className="episodes-container">
              {storyboardData.episodes?.map(episode => {
                const episodeScenes = episodeGroups[episode.id] || [];
                return (
                  <div key={episode.id} className="episode-section">
                    <div className="episode-header">
                      <h2>Episode {episode.episode_number}: {episode.title}</h2>
                      <div className="episode-info">
                        <span className="episode-scenes">Scenes {episode.start_scene_number}-{episode.end_scene_number}</span>
                        <span className="episode-count">{episodeScenes.length} scenes</span>
                      </div>
                    </div>
                    
                    <div className="episode-scenes">
                      <div className="scenes-horizontal-scroll">
                        {episodeScenes.map(scene => renderStoryboardSceneCard(scene))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Main render
  if (!isAuthenticated) {
    return renderLogin();
  }

  return (
    <>
      {viewMode === 'list' && renderProjectsList()}
      {viewMode === 'create' && renderCreateForm()}
      {viewMode === 'upload-scenario' && renderScenarioUpload()}
      {viewMode === 'generating' && renderGenerationProgress()}
      {viewMode === 'storyboard' && renderStoryboard()}
      
      {/* Modals */}
      {renderModal()}
      {renderSceneModal()}
      {renderVEO3Modal()}
      {renderVideoModal()}
      {renderAudioModal()}
      {renderImageEditModal()}
      {renderGlobalVideoModal()}
      
      {/* Floating Video Widget */}
      {renderFloatingVideoWidget()}
    </>
  );
}

export default App;